/**
 * PreEventReadinessPage — /analytics/template/pre-event-readiness/:eventId
 *
 * Pre-event checklist: countdown + meta + readiness score (locally
 * computable from /stats + /extras) + weather forecast (deferred) +
 * demographics (deferred) + competing events (deferred).
 *
 * V2.5-B Fork (2026-04-20). Critique HIGH #2: state machine fetches
 * /extras now so readiness score has its dependencies.
 *
 * Day-of transition: when stats.event.status === 'live' we surface a
 * banner pointing operators at /analytics/template/daily-live for
 * fresher signal — pre-event view is explicitly not the best place to
 * read live data.
 */

import { useEffect, useMemo, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import {
  BannerNote,
  DeferredPane,
  TemplateAnalyticsShell,
} from '../../components/analytics/TemplateAnalyticsShell'
import { CompactScoreTile } from '../../components/analytics/CompactScoreTile'
import { useEventAnalytics } from '../../components/analytics/useEventAnalytics'

function parseEventId(raw: string | undefined): number | null {
  if (!raw) return null
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null
}

const fmtDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'America/Los_Angeles',
    })
  } catch {
    return iso
  }
}

interface CountdownParts {
  days: number
  hours: number
  minutes: number
  isPast: boolean
}

function computeCountdown(eventDateIso: string | undefined, now: number): CountdownParts | null {
  if (!eventDateIso) return null
  const t = Date.parse(eventDateIso)
  if (!Number.isFinite(t)) return null
  const diff = t - now
  const isPast = diff <= 0
  const abs = Math.abs(diff)
  const days = Math.floor(abs / (24 * 60 * 60 * 1000))
  const hours = Math.floor((abs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000))
  const minutes = Math.floor((abs % (60 * 60 * 1000)) / (60 * 1000))
  return { days, hours, minutes, isPast }
}

export function PreEventReadinessPage() {
  const { setCurrentEventId } = useAuth()
  const params = useParams<{ eventId?: string }>()
  const eventId = parseEventId(params.eventId)

  useEffect(() => {
    if (eventId) setCurrentEventId(eventId)
  }, [eventId, setCurrentEventId])

  const { stats, statsLoading, statsError, extras, extrasAvailable } =
    useEventAnalytics({
      eventId,
      fetchCompare: false,
    })

  // Tick every 60s so the countdown stays current without re-rendering
  // on every second (spec: only announce day-boundary transitions).
  const [now, setNow] = useState<number>(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000)
    return () => window.clearInterval(id)
  }, [])

  const countdown = useMemo(
    () => computeCountdown(stats?.event.event_date, now),
    [stats, now],
  )

  // Readiness score: 5 checks, each worth 20 points.
  // 1. event_date set   2. status !== 'unknown'/'idle'
  // 3. plan_mix non-empty   4. kiosk_throughput non-empty
  // 5. landing HTML (deferred — always false today so max score is 80)
  const readinessScore = useMemo(() => {
    if (!stats) return null
    const checks: Array<[string, boolean]> = [
      ['Event date set', Boolean(stats.event.event_date)],
      [
        'Status assigned',
        Boolean(stats.event.status) &&
          !['unknown', 'idle', ''].includes(stats.event.status.toLowerCase()),
      ],
      [
        'At least one plan configured',
        extrasAvailable && (extras?.plan_mix?.length ?? 0) > 0,
      ],
      [
        'At least one kiosk configured',
        extrasAvailable && (extras?.kiosk_throughput?.length ?? 0) > 0,
      ],
    ]
    const passed = checks.filter(([, ok]) => ok).length
    return { pct: passed * 20, passed, total: checks.length, checks }
  }, [stats, extras, extrasAvailable])

  const isLiveAlready = stats?.event.status === 'live'

  if (eventId == null) {
    return (
      <TemplateAnalyticsShell title="Pre-event readiness">
        <div
          data-testid="pre-event-empty"
          className="rounded-card border border-dashed border-hairline bg-surface-stripe p-8 text-center text-sm text-ink-soft dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
        >
          Pick an upcoming event to see the pre-open checklist, weather
          forecast, and comparable history.
        </div>
      </TemplateAnalyticsShell>
    )
  }

  if (statsError && !stats) {
    return (
      <TemplateAnalyticsShell title="Pre-event readiness" eventName={null}>
        <BannerNote tone="warning" data-testid="pre-event-stats-error">
          Couldn&apos;t load event stats: {statsError}
        </BannerNote>
      </TemplateAnalyticsShell>
    )
  }

  return (
    <TemplateAnalyticsShell
      title="Pre-event readiness"
      subtitle={
        stats
          ? `${stats.event.event_name} · ${fmtDate(stats.event.event_date)}`
          : 'Loading…'
      }
      eventName={stats?.event.event_name ?? null}
      windowChip={stats ? stats.event.event_date : null}
      banner={
        isLiveAlready ? (
          <BannerNote data-testid="pre-event-live-banner">
            Event is live now —{' '}
            <Link
              to={`/analytics/template/daily-live/${eventId}`}
              className="font-semibold underline decoration-dashed underline-offset-2"
            >
              daily-live view
            </Link>{' '}
            has fresher signal.
          </BannerNote>
        ) : null
      }
    >
      <div
        className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
        data-testid="pre-event-grid"
      >
        {/* 1. Countdown + meta */}
        <section
          data-testid="pre-event-countdown"
          className="rounded-card border border-butler-200 bg-butler-50 p-5 shadow-card-soft dark:border-butler-700 dark:bg-butler-900/30"
        >
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-butler-700 dark:text-butler-200">
            {countdown?.isPast ? 'Opened' : 'Opens in'}
          </div>
          <div className="mt-2 font-display text-3xl font-bold tabular-nums text-butler-800 dark:text-butler-50">
            {countdown
              ? countdown.isPast
                ? `${countdown.days}d ago`
                : `${countdown.days}d ${countdown.hours}h ${countdown.minutes}m`
              : statsLoading
                ? '—'
                : 'Unscheduled'}
          </div>
          {stats ? (
            <div className="mt-3 space-y-1 text-xs text-ink-soft dark:text-slate-400">
              <div>
                <span className="font-semibold text-butler-700 dark:text-butler-200">
                  Event date
                </span>
                : {fmtDate(stats.event.event_date)}
              </div>
              <div>
                <span className="font-semibold text-butler-700 dark:text-butler-200">
                  Status
                </span>
                : {stats.event.status || 'unset'}
              </div>
            </div>
          ) : null}
        </section>

        {/* 2. Readiness score */}
        <CompactScoreTile
          label="Readiness score"
          scorePct={readinessScore ? readinessScore.pct : null}
          caption={
            readinessScore
              ? `${readinessScore.passed} of ${readinessScore.total} checks passed`
              : undefined
          }
          unavailableMessage="Pending event stats"
          data-testid="pre-event-readiness-score"
        />

        {/* 3. Readiness checklist — expose the booleans so operators can
            see which check failed. */}
        <section
          data-testid="pre-event-checklist"
          className="rounded-card border border-hairline bg-white p-4 shadow-card-soft dark:border-slate-700 dark:bg-slate-800"
        >
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-butler-700 dark:text-butler-200">
            Readiness checklist
          </div>
          {readinessScore ? (
            <ul className="mt-2 space-y-1.5 text-xs">
              {readinessScore.checks.map(([label, ok]) => (
                <li key={label} className="flex items-center gap-2">
                  <span
                    aria-hidden="true"
                    className={
                      ok
                        ? 'inline-block h-2 w-2 rounded-full bg-marquee-500'
                        : 'inline-block h-2 w-2 rounded-full bg-rose-500'
                    }
                  />
                  <span className={ok ? 'text-ink dark:text-slate-200' : 'text-ink-soft dark:text-slate-400'}>
                    {label}
                  </span>
                </li>
              ))}
              <li className="flex items-center gap-2 text-ink-soft dark:text-slate-400">
                <span
                  aria-hidden="true"
                  className="inline-block h-2 w-2 rounded-full bg-hairline-strong dark:bg-slate-600"
                />
                <span>Landing HTML staged (pending)</span>
              </li>
            </ul>
          ) : (
            <div className="mt-2 text-xs text-ink-soft dark:text-slate-400">
              Pending event stats…
            </div>
          )}
        </section>

        {/* 4. Weather forecast (deferred) */}
        <DeferredPane
          title="7-day weather forecast"
          message="Weather data isn't connected for this event yet."
        />

        {/* 5. Demographics (deferred) */}
        <DeferredPane
          title="Venue demographics"
          message="Census data for this venue hasn't loaded."
        />

        {/* 6. Competing events (deferred) */}
        <DeferredPane
          title="Competing events nearby"
          message="Competing-event feed isn't connected for this event."
        />
      </div>
    </TemplateAnalyticsShell>
  )
}
